import { useAppSelector } from "@/app/hooks";
import type {
  LeaseComp,
  LeaseCompFieldRule,
  LeaseCompFields,
} from "@/features/leaseComps/types/leaseComps.types";
import useTransformedImageUrl from "@/shared/hooks/useTransformedImage";
import { getLeaseCompIconData } from "@/shared/utils/map";
import React, { ReactNode, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import placeholderImage from "/image-placeholder-medium.png";

const formatFieldValue = (value: unknown): ReactNode => {
  if (value === undefined || value === null || value === "") {
    return "—";
  }

  if (typeof value === "string") {
    if (value.includes("\n")) {
      return (
        <div className="whitespace-pre-wrap">
          {value.split("\n").map((line, index) => {
            const urlPattern =
              /(https?:\/\/[^\s]+|www\.[^\s]+|[\w.-]+\.[a-z]{2,}(?:\/[^\s]*)?)/i;
            const urlMatch = urlPattern.exec(line);

            if (urlMatch) {
              const url = urlMatch[0];
              const beforeUrl = line.substring(0, urlMatch.index);
              const afterUrl = line.substring(urlMatch.index + url.length);
              return (
                <div key={index}>
                  {beforeUrl}
                  <a
                    href={url.startsWith("http") ? url : `https://${url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline"
                  >
                    {url.replace(/^https?:\/\//i, "")}
                  </a>
                  {afterUrl}
                </div>
              );
            }
            return <div key={index}>{line}</div>;
          })}
        </div>
      );
    }

    if (value.match(/^(https?:\/\/|www\.|[\w.-]+\.[a-z]{2,}(\/.*)?$)/i)) {
      return (
        <a
          href={value.startsWith("http") ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline"
        >
          {value.replace(/^https?:\/\//i, "")}
        </a>
      );
    }
  }

  return String(value);
};

const LeaseCompCard = ({
  comp,
  index,
  imageSrc,
  showPropertyPhoto = true,
  isHighlighted = false,
  cardRef,
  fieldRules = [],
}: {
  comp: LeaseComp;
  index: number;
  imageSrc?: string;
  showPropertyPhoto?: boolean;
  isHighlighted?: boolean;
  cardRef?: React.RefObject<HTMLDivElement | null>;
  fieldRules?: LeaseCompFieldRule[];
}) => {
  const fields = comp.leaseCompFields;

  const { transformedImageUrl } = useTransformedImageUrl(
    imageSrc !== placeholderImage ? imageSrc : undefined,
  );
  const resolvedImageSrc = transformedImageUrl || imageSrc;

  const hasFieldRules = fieldRules.length > 0;
  const isFieldActive = (keyName: string) =>
    fieldRules.some((r) => r.keyName === keyName && r.isActive);

  const nameFieldVisible = hasFieldRules ? isFieldActive("propertyName") : true;
  const addressFieldVisible = hasFieldRules
    ? isFieldActive("propertyAddress")
    : true;
  const cityFieldVisible = hasFieldRules ? isFieldActive("propertyCity") : true;
  const stateFieldVisible = hasFieldRules
    ? isFieldActive("propertyState")
    : true;
  const postalCodeFieldVisible = hasFieldRules
    ? isFieldActive("propertyPostalCode")
    : true;

  const nameValue = comp.name || fields.propertyName;
  const hasRealName =
    !!nameValue &&
    nameValue.trim() !== "" &&
    nameValue !== "Untitled Lease Comp";
  const nameIsTitle = nameFieldVisible && hasRealName;

  const displayTitle = nameIsTitle
    ? nameValue!
    : fields.propertyAddress || "Lease Comp";

  const formatAddress = (): string => {
    const stateZip = [
      stateFieldVisible ? fields.propertyState : null,
      postalCodeFieldVisible ? fields.propertyPostalCode : null,
    ]
      .filter(Boolean)
      .join("  ");
    const cityStateZip = [
      cityFieldVisible ? fields.propertyCity : null,
      stateZip || null,
    ].filter(Boolean);
    if (nameIsTitle) {
      const parts = [
        addressFieldVisible
          ? fields.propertyAddress?.replace(/,+\s*$/, "")
          : null,
        ...cityStateZip,
      ].filter(Boolean);
      return parts.join(", ");
    }
    return cityStateZip.join(", ");
  };

  const { iconColor } = getLeaseCompIconData(comp);

  return (
    <div
      ref={cardRef}
      className="property-card prop-card-col bg-white"
      style={{
        minHeight: "unset",
        paddingBottom: "0.5rem",
        borderBottom: isHighlighted
          ? "3px solid var(--color-cbre-green-light)"
          : undefined,
        boxShadow: isHighlighted
          ? "0 0 0 2px var(--color-cbre-green-light)"
          : undefined,
      }}
    >
      <div className="property-card-details">
        {/* Image with order badge */}
        {showPropertyPhoto ? (
          <div
            className="prop-bg-image"
            style={{
              backgroundImage: `url(${resolvedImageSrc || placeholderImage})`,
              pointerEvents: "none",
              cursor: "default",
            }}
          >
            <div
              className="prop-pen"
              style={{
                backgroundColor: iconColor,
                margin: "6px",
                borderRadius: "4px",
              }}
            >
              {index + 1}
            </div>
          </div>
        ) : (
          <div className="px-2 pt-2">
            <div
              className="prop-pen"
              style={{ backgroundColor: iconColor, borderRadius: "4px" }}
            >
              {index + 1}
            </div>
          </div>
        )}

        {/* Title and address */}
        <div className="property-details">
          <div className="flex flex-col gap-y-1">
            <h3 className="fancy prop-title truncate">{displayTitle}</h3>
            <p className="prop-card-address line-clamp-2">{formatAddress()}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

const InteractiveSurveyLeaseComps = () => {
  const { leaseComps, fieldRules, isLoading } = useAppSelector(
    (state) => state.leaseComps,
  );

  const { leaseMediaFiles } = useAppSelector((state) => state.leaseMedia);

  const showPropertyPhoto = useAppSelector(
    (state) =>
      state.interactiveSurvey.currentSurvey?.showPropertyPhoto !== false,
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get("highlight");
  const highlightRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlightId || !highlightRef.current || !scrollContainerRef.current)
      return;
    const container = scrollContainerRef.current;
    const card = highlightRef.current;
    // Scroll the card into the horizontal view of the overflow container
    const cardLeft = card.offsetLeft;
    const cardWidth = card.offsetWidth;
    const containerWidth = container.offsetWidth;
    container.scrollTo({
      left: cardLeft - containerWidth / 2 + cardWidth / 2,
      behavior: "smooth",
    });
  }, [highlightId]);

  useEffect(() => {
    if (!highlightId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        highlightRef.current &&
        !highlightRef.current.contains(e.target as Node)
      ) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("highlight");
          return next;
        });
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [highlightId, setSearchParams]);

  const visibleComps = leaseComps.filter((c) => !c.isHidden);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lease comps...</p>
        </div>
      </div>
    );
  }

  if (!leaseComps?.length) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <p className="text-gray-500">No lease comps available.</p>
      </div>
    );
  }

  const CARD_FIELDS = new Set([
    "propertyName",
    "propertyAddress",
    "propertyCity",
    "propertyState",
    "propertyPostalCode",
  ]);

  const activeFields = fieldRules
    .filter((f) => f.isActive && !CARD_FIELDS.has(f.keyName))
    .sort((a, b) => a.order - b.order);

  return (
    <div className="flex h-full bg-white">
      <div ref={scrollContainerRef} className="flex-1 mt-4 overflow-auto">
        {/* Sticky header with lease comp cards */}
        <div className="sticky top-0 z-20 bg-white">
          <div className="lease-comps-grid">
            {/* Empty label column header */}
            <div className="lease-comps-label-col" />

            {visibleComps.map((comp, index) => {
              const images = leaseMediaFiles[comp.leaseCompId]?.images ?? [];
              const imageSrc = images[0]?.cloudfrontUrl;
              const isHighlighted = comp.leaseCompId === highlightId;
              return (
                <LeaseCompCard
                  key={comp.leaseCompId}
                  comp={comp}
                  index={index}
                  imageSrc={imageSrc}
                  showPropertyPhoto={showPropertyPhoto}
                  isHighlighted={isHighlighted}
                  cardRef={isHighlighted ? highlightRef : undefined}
                  fieldRules={fieldRules}
                />
              );
            })}
          </div>
        </div>

        {/* Scrollable body with field rows */}
        <div className="bg-white">
          {activeFields.map((field, rowIndex) => (
            <div
              key={field.key}
              className={`lease-comps-grid lease-comps-field-row ${
                rowIndex % 2 === 0
                  ? "lease-comps-field-row--even"
                  : "lease-comps-field-row--odd"
              } ${rowIndex === 0 ? "border-t border-gray-300" : ""}`}
            >
              {/* Field label (sticky) */}
              <div
                className={`lease-comps-field-label ${
                  rowIndex % 2 === 0
                    ? "lease-comps-field-label--even"
                    : "lease-comps-field-label--odd"
                }`}
              >
                {field.displayName}
              </div>

              {/* Value per lease comp */}
              {visibleComps.map((comp) => {
                const value =
                  comp.leaseCompFields[field.keyName as keyof LeaseCompFields];
                return (
                  <div
                    key={`${comp.leaseCompId}-${field.key}`}
                    className="p-2 prop-prop-value wrap-break-word whitespace-pre-line"
                  >
                    {formatFieldValue(value)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InteractiveSurveyLeaseComps;
